import { getAssetStatuses } from "api/assets.api";
import { getMapAssets, getMapUsers } from "api/map.api";
import { getVenueFile } from "api/venue.api";
import { differenceInMinutes } from "date-fns";
import { FieldState, FormState } from "formstate";
import { min } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import { RootStore } from "stores/RootStore";
import { Asset, AssetStatus } from "types/asset";
import { User } from "types/userInfo";
import { BuildingGeoJsonObject, LevelGeoJsonObject, UnitGeoJsonObject, Venue } from "types/venue";
import { LabelValuePair } from "utils/common";
import { AssetPermissions } from "views/config/roles/RoleConfigDto";

const initialVenueObj: Venue = {
  venue_id: '',
  name: '',
  latitude: 0,
  longitude: 0,
  latest_versions: [{ name: '', dataset: '', released_at: '' }],
};

type FilterForm = FormState<{
  searchTerm: FieldState<string>,
  searchIn: FieldState<string>,
  show: FieldState<("assets" | "users")[]>,
  assetTypes: FieldState<string[]>,
  owners: FieldState<string[]>,
  assetStatuses: FieldState<string[]>,
  displayAssetName: FieldState<boolean>,
  displayUserName: FieldState<boolean>,
}>

export class MapStore {
  root: RootStore;
  loading = true;
  assetStatuses: AssetStatus[] = []
  assetTypes: LabelValuePair[] = []
  owners: LabelValuePair[] = []
  buildings!: BuildingGeoJsonObject
  levels!: LevelGeoJsonObject
  units!: UnitGeoJsonObject

  users: User[] = []
  assets: Asset[] = []

  selectedBuildingId: string | null = null
  selectedFloor: number | null = null
  selectedBeaconId: string | null = null

  GLOBAL_selectedMapVenueObj: Venue | null = null
  GLOBAL_buildingSelectedID: string | null = null
  GLOBAL_mapFloorOrdinal: number | null = null

  selectedVenueObj: Venue = initialVenueObj

  abortController = new AbortController();

  createFilterForm() {

    this.assetTypes = this.root.userRolePermissionStore.getAssetTypes(AssetPermissions.view)
    this.owners = this.root.userRolePermissionStore.getAssetOwners(AssetPermissions.view)

    return new FormState({
      searchTerm: new FieldState(""),
      searchIn: new FieldState(''),
      show: new FieldState<("assets" | "users")[]>(["assets", "users"]),
      assetTypes: new FieldState<string[]>(this.assetTypes.map(r => r.value)),
      owners: new FieldState<string[]>(this.owners.map(r => r.value)),
      assetStatuses: new FieldState<string[]>([...this.assetStatuses.map(r => r.asset_status_id), "__null"]),
      displayAssetName: new FieldState(false),
      displayUserName: new FieldState(false)
    })
  }

  filterForm: FilterForm

  constructor(root: RootStore) {
    this.root = root;

    makeObservable(this, {
      loading: observable,
      buildings: observable,
      levels: observable,
      units: observable,
      selectedBuildingId: observable,
      selectedFloor: observable,
      selectedBeaconId: observable,
      filterForm: observable,
      users: observable,
      assets: observable,
      abortController: observable,

      reset: action.bound,
      loadData: action.bound,
      selectBuilding: action.bound,
      selectFloor: action.bound,
      selectBeacon: action.bound,
      getBuildingLevels: action.bound,
      getBuildingAssets: action.bound,
      getBuildingUsers: action.bound,
      getBuidlingAssetsCount: action.bound,
      getBuidlingUsersCount: action.bound,
      getOrdinalLevels: action.bound,
      getOrdinalUnits: action.bound,
      loadAssets: action.bound,
      loadUsers: action.bound,
      getFirstNotEmptyFloor: action.bound,
      getAssetSearchParams: action.bound,
      resetFilterForm: action.bound,

      selectedBuildingOrdinals: computed,
      selectedBuildingLevels: computed,
      outdoorAssets: computed,
      outdoorUsers: computed,
      showAssetMarkers: computed,
      showUserMarkers: computed,
    });

    this.filterForm = this.createFilterForm()
  }

  reset() {
    this.loading = true;
    this.users = []
    this.assets = []

    this.selectedBuildingId = null
    this.selectedFloor = null
    this.selectedBeaconId = null

    this.selectedVenueObj = initialVenueObj
    this.abortController = new AbortController();
    this.filterForm = this.createFilterForm()

  }

  resetFilterForm() {
    this.filterForm = this.createFilterForm()
  }

  selectBuilding(building_id: string | null) {
    this.selectedBuildingId = building_id
    const ordinals = this.getBuildingOrdinals(building_id)
    this.selectFloor(ordinals.length > 0 ? ordinals[0] : null)
  }
  selectFloor(floor: number | null) {
    this.selectedFloor = floor
    this.selectedBeaconId = null
  }
  selectBeacon(id: string | null) {
    this.selectedBeaconId = id
  }

  getActiveUsers(users: User[]) {

    const dateNow = new Date();
    return users.filter((user) => {
      const formattedEstimateDate = new Date(user.last_update_timestamp);
      const diffInMins = differenceInMinutes(dateNow, formattedEstimateDate);
      // returns true if last update time is before time last hour from now
      return diffInMins >= 0 && diffInMins < 60;
    });
  }

  getAssetSearchParams() {
    const params: Record<string, any> = {}
    if (this.filterForm.$.searchTerm.value !== "" && this.filterForm.$.searchIn.value === "assets")
      params["name_filter"] = this.filterForm.$.searchTerm.value

    if (this.filterForm.$.assetTypes.value.length < this.assetTypes.length)
      params["asset_type_ids_filter"] = this.filterForm.$.assetTypes.value
    if (this.filterForm.$.assetTypes.value.length === 0)
      params["asset_type_ids_filter"] = null

    if (this.filterForm.$.owners.value.length < this.owners.length)
      params["owner_ids_filter"] = this.filterForm.$.owners.value
    if (this.filterForm.$.owners.value.length === 0)
      params["owner_ids_filter"] = null

    if (this.filterForm.$.assetStatuses.value.length < this.assetStatuses.length + 1)
      params["status_ids_filter"] = this.filterForm.$.assetStatuses.value
    if (this.filterForm.$.assetStatuses.value.length === 0)
      params["status_ids_filter"] = null

    return params
  }

  updateSelectedBeacon(assets: Asset[]) {
    //if selected beacon is not in assets list, deselect it
    if (this.selectedBeaconId) {
      if (!assets.find(asset => asset.assigned_beacon!.id === this.selectedBeaconId))
        this.selectBeacon(null)
    }
  }

  loadAssets() {
    const params = this.getAssetSearchParams()
    return getMapAssets(this.root.venue.venue_id, params).then(res => {
      this.assets = res.items
      this.updateSelectedBeacon(res.items)
      return res.items
    })
  }
  loadUsers() {

    const params: Record<string, any> = {}
    if (this.filterForm.$.searchTerm.value !== "" && this.filterForm.$.searchIn.value === "users")
      params["name_filter"] = this.filterForm.$.searchTerm.value

    return getMapUsers(this.root.venue.venue_id, params)
      .then(res => {
        this.users = this.getActiveUsers(res.items)
        return res.items
      })
  }

  loadData() {
    this.loading = true
    const venue_id = this.root.venue.venue_id
    const venueUrl = this.root.webConfig.services.venue_service

    return Promise.all([
      this.root.userRolePermissionStore.canViewAssets ? getAssetStatuses() : [],
      getVenueFile(venueUrl, venue_id, 'building.geojson'),
      getVenueFile(venueUrl, venue_id, 'level.geojson'),
      getVenueFile(venueUrl, venue_id, 'unit.geojson'),
      this.root.userRolePermissionStore.canViewAssets ? getMapAssets(venue_id) : Promise.resolve(null),
      this.root.userRolePermissionStore.canViewUsers ? getMapUsers(venue_id) : Promise.resolve(null),
    ]).then(async (res) => {
      this.assetStatuses = res[0]
      this.buildings = res[1]
      this.levels = res[2]
      this.units = res[3]
      if (res[4])
        this.assets = res[4].items
      if (res[5])
        this.users = this.getActiveUsers(res[5].items)

      this.filterForm = this.createFilterForm()
      this.loading = false
    })
  }

  getBuildingLevels(building_id: string | null) {
    if (!building_id)
      return []
    return this.levels.features.filter(feature =>
      feature.properties.building_ids?.includes(building_id)
    )
  }

  getBuildingOrdinals(building_id: string | null): number[] {
    if (!building_id)
      return []
    return this.getBuildingLevels(building_id)
      .filter((value: any, index: number, self: any) => {
        return self.findIndex((v: any) => v.properties.ordinal === value.properties.ordinal) === index;
      })
      .sort((a, b) => a.properties.ordinal > b.properties.ordinal ? 1 : -1)
      .map(r => r.properties.ordinal)
  }

  get selectedBuildingLevels() {
    return this.getBuildingLevels(this.selectedBuildingId)
  }

  get selectedBuildingOrdinals() {
    return this.getBuildingOrdinals(this.selectedBuildingId)
  }

  getOrdinalLevels(building_id: string, ordinal: number) {
    return this.getBuildingLevels(building_id).filter(f => f.properties.ordinal === ordinal)
  }

  getOrdinalUnits(building_id: string, ordinal: number) {
    const levels = this.getOrdinalLevels(building_id, ordinal).map(r => r.id)
    return this.units.features.filter(feature => levels.includes(feature.properties.level_id))
  }

  getBuildingAssets(building_id: string | null): Record<number, Asset[]> {
    if (!building_id)
      return {}
    const list: Record<number, Asset[]> = {}
    this.getBuildingOrdinals(building_id).forEach(ordinal => {
      const levels = this.getBuildingLevels(building_id).filter(f => f.properties.ordinal === ordinal)
      list[ordinal] = this.assets
        .filter(asset =>
          asset.assigned_beacon !== null &&
          asset.location.building_level !== null &&
          asset.location.building_level !== undefined &&
          asset.location.building_level.possible_buildings.length > 0 &&
          !asset.location.is_outdoors)
        .filter(asset => levels.map(r => r.id).includes(asset.location.building_level.level.id))
    })
    return list
  }

  getBuidlingAssetsCount(building_id: string | null) {
    const list = this.getBuildingAssets(building_id)
    return Object.values(list).reduce((sum, v) => sum + v.length, 0)
  }

  getBuildingUsers(building_id: string | null): Record<number, User[]> {
    if (!building_id)
      return {}
    const list: Record<number, User[]> = {}
    this.getBuildingOrdinals(building_id).forEach(ordinal => {
      const levels = this.getBuildingLevels(building_id).filter(f => f.properties.ordinal === ordinal)
      list[ordinal] = this.users
        .filter(user =>
          user.location &&
          user.location.coordinates &&
          user.location.building_level &&
          !user.location.is_outdoors)
        .filter(user => levels.map(r => r.id).includes(user.location.building_level.level.id))
    })
    return list
  }

  getBuidlingUsersCount(building_id: string | null) {
    const list = this.getBuildingUsers(building_id)
    return Object.values(list).reduce((sum, v) => sum + v.length, 0)
  }

  getFirstNotEmptyFloor(building_id: string): number {

    let ordinals: Record<number, any[]> = {}

    if (this.showAssetMarkers)
      ordinals = this.getBuildingAssets(building_id)
    else
      ordinals = this.getBuildingUsers(building_id)

    const ordinalsList = Object.keys(ordinals).sort((a, b) => parseInt(a) > parseInt(b) ? 1 : -1)
    for (const ordinal of ordinalsList)
      if (ordinals[parseInt(ordinal)].length > 0)
        return parseInt(ordinal)

    return min(Object.keys(ordinals).map(r => parseInt(r))) || 0

  }

  get outdoorAssets() {
    return this.assets.filter(asset => asset.assigned_beacon && asset.location.is_outdoors)
  }

  get outdoorUsers() {
    return this.users.filter(user => user.location.is_outdoors)
  }

  get showAssetMarkers() {
    return this.filterForm.$.show.value.includes("assets")
      && this.filterForm.$.searchIn.value !== "users"
  }

  get showUserMarkers() {
    return this.filterForm.$.show.value.includes("users")
      && this.filterForm.$.searchIn.value !== "assets"
  }


}
